import { useState, useCallback } from 'react';
import axios from 'axios';
import { UserActivityRow, PharmacyDistRow } from '../types';
import { API_BASE } from '../constants';

export interface UsersActivityState {
  usersActivity: UserActivityRow[];
  usersActivityLoading: boolean;
  activitySearch: string;
  setActivitySearch: (v: string) => void;
  activityLoaded: boolean;
  activityMonth: string;
  fetchUsersActivity: (month: string) => Promise<void>;
}

/**
 * Загружает и мержит данные об активности пользователей за выбранный месяц.
 *
 * Алгоритм:
 * 1. Получает список всех пользователей с привязкой к аптеке (`/api/users-with-pharmacy`)
 * 2. Получает статистику по аптекам за период (`/journal/analytics/pharmacy-distribution`)
 * 3. Объединяет два источника, выводя пользователей без записей в начало (сортировка)
 */
export function useUsersActivity(): UsersActivityState {
  const [usersActivity, setUsersActivity] = useState<UserActivityRow[]>([]);
  const [usersActivityLoading, setUsersActivityLoading] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityMonth, setActivityMonth] = useState('');

  const fetchUsersActivity = useCallback(async (month: string) => {
    setUsersActivityLoading(true);
    try {
      const [usersResp, distResp] = await Promise.all([
        axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy'),
        axios.get(`${API_BASE}/journal/analytics/pharmacy-distribution`, {
          params: month && month !== 'all' ? { month } : {},
        }),
      ]);

      const allUsers: { username: string; pharmacyNum: string }[] =
        usersResp.data?.success ? usersResp.data.users : [];

      const distMap = new Map<string, { count: number; totalSum: number }>();
      if (distResp.data?.success) {
        (distResp.data.data as PharmacyDistRow[]).forEach(r => {
          distMap.set(String(r.PharmacyNum), { count: r.count, totalSum: r.total });
        });
      }

      const merged: UserActivityRow[] = allUsers.map(u => {
        const stat = distMap.get(String(u.pharmacyNum));
        return {
          username: u.username,
          pharmacyNum: String(u.pharmacyNum),
          recordCount: stat?.count ?? 0,
          totalSum: stat?.totalSum ?? 0,
        };
      });

      // Пользователи без записей — в начало, внутри групп — по имени
      merged.sort((a, b) => {
        if (a.recordCount === 0 && b.recordCount > 0) return -1;
        if (a.recordCount > 0 && b.recordCount === 0) return 1;
        return a.username.localeCompare(b.username);
      });

      setUsersActivity(merged);
      setActivityLoaded(true);
      setActivityMonth(month);
    } catch (e) {
      console.error('Users activity fetch error:', e);
    } finally {
      setUsersActivityLoading(false);
    }
  }, []);

  return {
    usersActivity, usersActivityLoading,
    activitySearch, setActivitySearch,
    activityLoaded, activityMonth,
    fetchUsersActivity,
  };
}
